import React, { useEffect } from "react";
import { Modal } from "@mui/material";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../components/ui";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import InlineLoader from "../../components/loader/InlineLoader";
import { XIcon } from "@heroicons/react/solid";

const AddLeaveManagementModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  userData,
  firstDate,
  lastDate,
  formatDate,
  leaveStructureData,
  isLeaveStructureLoading,
  isUserDataLoading,
  calculateAvailableLeaves,
}) => {
  const {
    getValues,
    control,
    setValue,
    reset,
    formState,
    register,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      leaveStructure: [],
    },
  });

  useEffect(() => {
    if (
      leaveStructureData?.data?.leave_structure?.length > 0 &&
      isOpen &&
      !isSubmitting
    ) {
      console.log("Initializing form with leave structure data");

      const leaveStructure = leaveStructureData.data.leave_structure.map(
        (leave) => ({
          leave_head_id: leave.leave_head_id,
          leave_head_name: leave.leave_head_name,
          leave_head_type: leave.leave_head_type,
          annual: leave.annual === "" ? "0" : leave.annual,
          available: leave.available === "" ? "0" : leave.available,
        })
      );

      // Populate form fields for each leave type
      leaveStructure.forEach((leave, index) => {
        setValue(`leaveStructure[${index}].leave_head_id`, leave.leave_head_id);
        setValue(
          `leaveStructure[${index}].leave_head_name`,
          leave.leave_head_name
        );
        setValue(
          `leaveStructure[${index}].leave_head_type`,
          leave.leave_head_type
        );
        setValue(`leaveStructure[${index}].annual`, leave.annual);
        setValue(`leaveStructure[${index}].available`, leave.available);
      });
    }
  }, [leaveStructureData, isOpen, isSubmitting]);

  const handleAnnualLeavesChange = (leaveIndex) => {
    const annualLeaves = parseInt(
      getValues(`leaveStructure[${leaveIndex}].annual`)
    );
    const dateOfJoiningRaw = userData?.data?.data.date_of_joining;

    // Function to calculate available leaves
    const availableLeaves = calculateAvailableLeaves(
      annualLeaves,
      dateOfJoiningRaw
    );
    setValue(
      `leaveStructure[${leaveIndex}].available`,
      availableLeaves.toString()
    );
  };

  const handleFormSubmit = (data) => {
    onSubmit(data.leaveStructure);
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  // Show loader if either user data or leave structure is loading
  const isDataLoading = isLeaveStructureLoading || isUserDataLoading;

  return (
    <Modal open={isOpen} onClose={handleCancel}>
      <div className="bg-white p-6 rounded shadow-lg w-[90%] max-w-2xl mx-auto mt-16 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold">Add Leave Management</h2>
          <Button variant={"ghost"} size={"icon"} onClick={handleCancel}>
            <XIcon className="text-gray-600 w-full h-full hover:text-gray-800" />
          </Button>
        </div>

        {isDataLoading ? (
          <div className="text-center py-8">
            <PrimaryLoader />
            <p className="mt-3 text-gray-600">Loading data...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Input
                  label="First Date of the Year"
                  disabled
                  type="text"
                  name="firstDate"
                  control={control}
                  value={formatDate(firstDate)}
                />
              </div>
              <div>
                <Input
                  label="Last Date of the Year"
                  disabled
                  type="text"
                  name="lastDate"
                  control={control}
                  value={formatDate(lastDate)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <Input
                  label="Date Of Joining"
                  disabled
                  type="text"
                  name="dateOfJoining"
                  control={control}
                  value={
                    userData?.data?.data.date_of_joining
                      ? new Date(
                          userData.data.data.date_of_joining * 1000
                        ).toLocaleDateString("en-GB")
                      : "N/A"
                  }
                />
              </div>
              <div>
                <Input
                  label="Probation Date"
                  disabled
                  type="text"
                  name="probationDate"
                  control={control}
                  value={
                    userData?.data?.data.date_of_probation
                      ? new Date(
                          userData.data.data.date_of_probation * 1000
                        ).toLocaleDateString("en-GB")
                      : "N/A"
                  }
                />
              </div>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <div className="space-y-6">
                {leaveStructureData?.data?.leave_structure?.map(
                  (leaveType, index) => (
                    <div
                      key={leaveType.leave_head_id}
                      className="border p-4 rounded-md"
                    >
                      <h3 className="font-semibold text-lg mb-3">
                        {leaveType.leave_head_name}
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Annual Leaves"
                          type="number"
                          name={`leaveStructure[${index}].annual`}
                          control={control}
                          rules={{
                            required: "This field is required",
                            min: {
                              value: 0,
                              message: "Value must be 0 or greater",
                            },
                          }}
                          onChangeFn={() => handleAnnualLeavesChange(index)}
                        />
                        <Input
                          label="Available Leaves"
                          type="number"
                          name={`leaveStructure[${index}].available`}
                          control={control}
                          rules={{
                            required: "This field is required",
                            min: {
                              value: 0,
                              message: "Value must be 0 or greater",
                            },
                          }}
                        />

                        {/* Hidden inputs for leave type information */}
                        <input
                          type="hidden"
                          {...register(
                            `leaveStructure[${index}].leave_head_id`
                          )}
                          defaultValue={leaveType.leave_head_id}
                        />
                        <input
                          type="hidden"
                          {...register(
                            `leaveStructure[${index}].leave_head_name`
                          )}
                          defaultValue={leaveType.leave_head_name}
                        />
                        <input
                          type="hidden"
                          {...register(
                            `leaveStructure[${index}].leave_head_type`
                          )}
                          defaultValue={leaveType.leave_head_type}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <Button type="button" onClick={handleCancel} variant="outline">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={` ${isSubmitting ? "bg-gray-400" : ""}`}
                >
                  {isSubmitting ? (
                    <>
                      <InlineLoader className="mr-2" /> Adding...
                    </>
                  ) : (
                    "Add Leave Management"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AddLeaveManagementModal;
